import { CoordsNormalizerI } from 'src/app/interfaces/map.interface';
import { Coords } from 'src/app/swagger-api';
import MapSettings from '../../map.settings';

export class CoordsNormalizer implements CoordsNormalizerI {
  public constructor(private coords: Coords) {}

  public getNormalizedCoords(): Coords {
    return this.coords;
  }
}

export class CoordsNormalizerDecorator implements CoordsNormalizerI {
  public constructor(protected entity: CoordsNormalizerI) {}

  public getNormalizedCoords(): Coords {
    return {
      lat: this.getValidLatitude(this.entity.getNormalizedCoords().lat),
      lon: this.getValidLongitude(this.entity.getNormalizedCoords().lon),
    };
  }

  /**
   * @description Returns the normalized value of longitude.
   * @param { number } longitude
   * @return { number }
   */
  public getValidLongitude(longitude: number): number {
    if (this.isLongitude(longitude)) {
      return longitude;
    }

    return this.getClosest(longitude, [MapSettings.LONGITUDE_MIN_VALUE, MapSettings.LONGITUDE_MAX_VALUE]);
  }

  /**
   * @description Returns the normalized value of latitude.
   * @param { number } longitude
   * @return { number }
   */
  public getValidLatitude(latitude: number): number {
    if (this.isLatitude(latitude)) {
      return latitude;
    }

    return this.getClosest(latitude, [MapSettings.LATITUDE_MIN_VALUE, MapSettings.LATITUDE_MAX_VALUE]);
  }

  /**
   * @description Checks the validity of the latitude value. Rule: -90 <= value >= 90.
   *    The Number.isFinite() method determines whether the passed value is a finite
   *    number — that is, it checks that a number is neither positive nor negative Infinity.
   *    The Math.abs() function returns the absolute value of a number.
   * @param { number } value
   * @return { boolean }
   */
  public isLatitude(value: number): boolean {
    return isFinite(value) && Math.abs(value) <= MapSettings.LATITUDE_MAX_VALUE;
  }

  /**
   * @description Checks the validity of the longitude value. Rule: -180 <= value >= 180.
   * @param { number } value
   * @return { boolean }
   */
  public isLongitude(value: number): boolean {
    return isFinite(value) && Math.abs(value) <= MapSettings.LONGITUDE_MAX_VALUE;
  }

  /**
   * @description Finds the closest value in array using reduce() function.
   *              Checks whether the absolute value of (b – 8) is less than the absolute
   *              value of (a – 8) and then return the winner.
   * @param { number } value
   * @param { number[] } counts
   * @return { number }
   */
  public getClosest(value: number, counts: number[]): number {
    return counts.reduce((prev: number, curr: number) =>
      Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev,
    );
  }
}
